import React, { useState, useRef, useEffect } from 'react';
import { ChevronDown, ChevronRight, Search, X, Check } from 'lucide-react';
import { useTranslation } from '../hooks/useTranslation';
import { getAllCategories, getCategoryPathLabel, searchCategories } from '../services/categoryService';

const CategorySelector = ({ selectedCategory, onSelect, className = '' }) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedCategories, setExpandedCategories] = useState([]);
  const dropdownRef = useRef(null);
  const inputRef = useRef(null);

  const categories = getAllCategories();
  const searchResults = searchCategories(searchQuery);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
        setSearchQuery('');
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  const toggleCategory = (path) => {
    setExpandedCategories(prev => 
      prev.includes(path) 
        ? prev.filter(p => p !== path)
        : [...prev, path]
    );
  };

  const handleSelect = (path) => {
    onSelect(path);
    setIsOpen(false);
    setSearchQuery('');
  };

  const renderCategory = (key, category, path, depth = 0) => {
    const hasSubcategories = category.subcategories && Object.keys(category.subcategories).length > 0;
    const isExpanded = expandedCategories.includes(path);
    const isSelected = selectedCategory === path;
    const Icon = category.icon;
    
    // Extract label based on current language
    const lang = localStorage.getItem('language') || 'en';
    const label = typeof category.label === 'string' 
      ? category.label 
      : (category.label[lang] || category.label.en);

    return (
      <div key={path} className={`category-item depth-${depth}`}>
        <div
          className={`category-row ${isSelected ? 'selected' : ''}`}
          onClick={() => {
            if (hasSubcategories) {
              toggleCategory(path);
            } else {
              handleSelect(path);
            }
          }}
        >
          <div className="flex items-center flex-1">
            {hasSubcategories ? (
              <button
                type="button"
                className="expand-button"
                onClick={(e) => {
                  e.stopPropagation();
                  toggleCategory(path);
                }}
              >
                {isExpanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
              </button>
            ) : (
              <div className="w-4" />
            )}
            <Icon className="category-icon" size={18} />
            <span className="category-name">{label}</span>
            {isSelected && <Check size={16} className="ml-auto text-primary-600" />}
          </div>
        </div>
        
        {hasSubcategories && isExpanded && (
          <div className="subcategories ml-6">
            {Object.keys(category.subcategories).map(subKey => 
              renderCategory(subKey, category.subcategories[subKey], `${path}.${subKey}`, depth + 1)
            )}
          </div>
        )}
      </div>
    );
  };

  const selectedLabel = selectedCategory 
    ? getCategoryPathLabel(selectedCategory) 
    : t('wishForm.noCategory');

  return (
    <div className={`category-selector ${className}`}>
      <label className="block text-sm font-medium text-gray-700 mb-1">
        {t('wishForm.category')}
      </label>
      
      <div className="relative" ref={dropdownRef}>
        <button
          type="button"
          onClick={() => setIsOpen(!isOpen)}
          className="input-field flex items-center justify-between w-full"
        >
          <span className={selectedCategory ? 'text-gray-900' : 'text-gray-400'}>
            {selectedLabel}
          </span>
          <ChevronDown size={20} className="text-gray-400" />
        </button>

        {isOpen && (
          <div className="category-dropdown">
            <div className="search-container">
              <Search size={18} className="search-icon" />
              <input
                ref={inputRef}
                type="text"
                placeholder={t('wishForm.searchCategories')}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="search-input"
              />
              {searchQuery && (
                <button
                  type="button"
                  onClick={() => setSearchQuery('')}
                  className="clear-search"
                >
                  <X size={16} />
                </button>
              )}
            </div>

            <div className="category-list">
              {searchQuery ? (
                // Show search results
                searchResults.length > 0 ? (
                  searchResults.map(result => {
                    const Icon = result.icon;
                    return (
                      <div
                        key={result.path}
                        className={`category-row ${selectedCategory === result.path ? 'selected' : ''}`}
                        onClick={() => handleSelect(result.path)}
                      >
                        <Icon className="category-icon" size={18} />
                        <span className="category-name">{result.label}</span>
                      </div>
                    );
                  })
                ) : (
                  <div className="no-results">{t('wishForm.noResults')}</div>
                )
              ) : (
                // Show full category tree
                <>
                  {Object.keys(categories).map(key => 
                    renderCategory(key, categories[key], key)
                  )}
                </>
              )}
            </div>

            {selectedCategory && (
              <div className="category-footer">
                <button
                  type="button"
                  onClick={() => handleSelect(null)}
                  className="clear-button"
                >
                  <X size={16} />
                  <span>{t('common.clear')}</span>
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      <p className="mt-1 text-sm text-gray-500">
        {t('wishForm.categoryOptional')}
      </p>
    </div>
  );
};

export default CategorySelector;

